import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlProps,
    InputLabelProps,
    SelectProps,
} from '@mui/material';

interface DataFilterCriteria<T = any> {
    field: keyof T;
    operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'in' | 'gt' | 'gte' | 'lt' | 'lte';
    value: T[keyof T] | Array<T[keyof T]>;
}

interface DataFilterRequest<T = any> {
    filters: Array<DataFilterCriteria<T>>;
    page?: number;
    pageSize?: number;
    sortField?: keyof T;
    sortOrder?: 'asc' | 'desc';
}

interface DataFilterControlProps<T> {
    filterRequest: DataFilterRequest<T>;
    onFilterChange: (newRequest: DataFilterRequest<T>) => void;
    field: keyof T;
    label: string;
    options: T[keyof T][];
    operator?: DataFilterCriteria<T>['operator'];
    slotProps?: {
        formControl?: FormControlProps;
        inputLabel?: InputLabelProps;
        select?: SelectProps;
    };
}

const DataFilterControl = <T,>({
    filterRequest,
    onFilterChange,
    field,
    label,
    options,
    operator = 'equals',
    slotProps = {},
}: DataFilterControlProps<T>) => {
    const existing = filterRequest.filters.find((f) => f.field === field);
    const value = (existing?.value as string | '') ?? '';

    const handleChange = (newValue: any) => {
        const newFilters = filterRequest.filters.filter((f) => f.field !== field);
        if (newValue !== '' && newValue != null) {
            newFilters.push({
                field,
                operator,
                value: newValue,
            });
        }
        onFilterChange({
            ...filterRequest,
            filters: newFilters,
            page: 1,
        });
    };

    return (
        <FormControl variant="outlined" size="small" sx={{ width: '100%' }} {...slotProps?.formControl}>
            <InputLabel shrink {...slotProps?.inputLabel}>
                {label}
            </InputLabel>
            <Select
                label={label}
                displayEmpty
                {...slotProps?.select}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
            >
                <MenuItem value="">
                    <em>All</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem key={String(option)} value={option as string}>
                        {String(option)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export type { DataFilterCriteria, DataFilterRequest };
export type { DataFilterControlProps };
export { DataFilterControl };
